import { useAuth } from '@clerk/clerk-react';
import { contract } from '@ditto/shared';
import {
  UseQueryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { initClient, tsRestFetchApi } from '@ts-rest/core';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    message: string,
    public error: string,
    public timestamp: string,
    public path: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Use this hook to create a client that is authenticated with the current user.
 * If withWorkspace is true, the client will be authenticated with the current workspace.
 */
export const useAuthenticatedClient = (withWorkspace = true) => {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  const backendUrl = withWorkspace ? useWorkspaceBaseUrl() : baseUrl;
  const client = useMemo(
    () =>
      initClient(contract, {
        baseUrl: backendUrl,
        api: async args => {
          const token = await getToken();
          args.headers = {
            ...args.headers,
            Authorization: `Bearer ${token}`,
          };

          const response = await tsRestFetchApi(args);

          // Check for 404 responses and throw ApiError
          if (response && response.status === 404) {
            throw new ApiError(
              response.status,
              (response.body as { message?: string })?.message ?? 'API Error',
              (response.body as { error?: string })?.error ?? 'API Error',
              new Date().toISOString(),
              backendUrl
            );
          }

          return response;
        },
      }),
    [getToken]
  );

  return { client, isLoaded, isSignedIn };
};

export const useProtectedQuery = <
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = unknown[],
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> => {
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  return useQuery({
    ...options,
    enabled: (options.enabled ?? true) && isLoaded && isSignedIn,
  });
};

// Base URL for direct fetch calls (like file uploads)
export const getBaseUrl = () => baseUrl;

export const useWorkspaceBaseUrl = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  return `${baseUrl}/${workspaceUname}`;
};
